import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon, Eye, Download, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FilePreview } from "@/components/expenses/FilePreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.doc,.docx,.xls,.xlsx,.csv,.txt,application/pdf,image/*";
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const size = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const Icon = ({ type }) => type.includes("pdf") ? <FileText className="w-4 h-4 text-red-600" /> : type.startsWith("image/") ? <ImageIcon className="w-4 h-4 text-blue-600" />
  : type.includes("sheet") || type.includes("excel") || type.includes("csv") ? <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> : <FileIcon className="w-4 h-4 text-slate-500" />;
const previewable = (t) => t.includes("pdf") || t.startsWith("image/");

const downloadDoc = async (d) => {
  const res = await axios.get(`${API}/customers/${d.customer_id}/documents/${d.id}/download?download=1`, { responseType: "blob" });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a"); a.href = href; a.download = d.filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
};

export const CustomerDocuments = ({ customerId, canEdit, onCountChange }) => {
  const [docs, setDocs] = useState(null);
  const [desc, setDesc] = useState("");
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [preview, setPreview] = useState(null);
  const load = () => axios.get(`${API}/customers/${customerId}/documents`).then((r) => { setDocs(r.data); onCountChange?.(r.data.length); }).catch(() => toast.error("Could not load documents"));
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(list.length);
    for (const f of list) {
      const fd = new FormData(); fd.append("file", f); fd.append("description", desc);
      try { await axios.post(`${API}/customers/${customerId}/documents`, fd); toast.success(`Uploaded ${f.name}`); }
      catch (e) { toast.error(`${f.name}: ${e.response?.data?.detail || "Upload failed"}`); }
      setUploading((n) => n - 1);
    }
    setDesc("");
    load();
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"?`)) return;
    try { await axios.delete(`${API}/customers/${customerId}/documents/${d.id}`); toast.success("Document deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="space-y-3" data-testid="customer-documents">
      {canEdit && (
        <div className="space-y-2">
          <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional) – e.g. Signed contract 2026" data-testid="customer-doc-description" />
          <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
            className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-xl p-5 cursor-pointer transition-colors ${drag ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400"}`} data-testid="customer-doc-dropzone">
            {uploading ? <Loader2 className="w-6 h-6 text-blue-600 animate-spin" /> : <Upload className="w-6 h-6 text-blue-600" />}
            <p className="text-sm font-medium text-slate-800">{uploading ? `Uploading ${uploading} file${uploading > 1 ? "s" : ""}…` : "Drop files here or tap to choose"}</p>
            <p className="text-[11px] text-slate-500 text-center">PDF, images, Word, Excel or TXT · up to 20 MB each</p>
            <input type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="customer-doc-file-input" />
          </label>
        </div>
      )}
      {docs === null ? <div className="flex justify-center py-4"><Loader2 className="animate-spin text-slate-400" /></div>
        : docs.length === 0 ? <p className="text-sm text-slate-400" data-testid="customer-documents-empty">No documents yet</p>
        : docs.map((d) => (
          <div key={d.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm" data-testid={`customer-doc-${d.id}`}>
            <Icon type={d.content_type} />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-800 truncate" title={d.filename} data-testid="customer-doc-name">{d.filename}</p>
              {d.description && <p className="text-xs text-slate-600 truncate">{d.description}</p>}
              <p className="text-xs text-slate-500">{size(d.size)} · {d.uploaded_by_name || d.uploaded_by} · {when(d.uploaded_at)}</p>
            </div>
            <div className="flex gap-0.5 shrink-0">
              {previewable(d.content_type) && <Button size="icon" variant="ghost" className="h-7 w-7" title="View" onClick={() => setPreview(d)} data-testid="customer-doc-view"><Eye className="w-4 h-4" /></Button>}
              <Button size="icon" variant="ghost" className="h-7 w-7 text-green-700" title="Download" onClick={() => downloadDoc(d).catch(() => toast.error("Download failed"))} data-testid="customer-doc-download"><Download className="w-4 h-4" /></Button>
              {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:bg-red-50" title="Delete" onClick={() => remove(d)} data-testid="customer-doc-delete"><Trash2 className="w-4 h-4" /></Button>}
            </div>
          </div>
        ))}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="sm:max-w-[900px] max-h-[92vh] overflow-y-auto" data-testid="customer-doc-preview-dialog">
          <DialogHeader><DialogTitle className="truncate pr-6">{preview?.filename}</DialogTitle></DialogHeader>
          {preview && <FilePreview url={`${API}/customers/${customerId}/documents/${preview.id}/download`} contentType={preview.content_type} testPrefix="customer-doc" />}
          {preview?.description && <p className="text-sm text-slate-600">{preview.description}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
};
